import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, HelpCircle, Mail, Check, Image as ImageIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { GEMEENTEN } from "@/constants/testIds";
import SectionLabel from "@/components/SectionLabel";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import ProgrammaArchitectuur from "@/components/ProgrammaArchitectuur";
import { Squiggle, SquiggleUnderline, RouteLine, FloatingBlob } from "@/components/Squiggle";
import { usePerspective } from "@/context/PerspectiveContext";
import PilotTimeline from "@/components/PilotTimeline";
import PositieInKeten from "@/components/PositieInKeten";
import IntegraalDomeinen from "@/components/IntegraalDomeinen";
import LeafletKaart from "@/components/LeafletKaart";
import OnderzoekKaarten from "@/components/OnderzoekKaarten";
import ZesPijlers from "@/components/ZesPijlers";
import DeelnemerJourney from "@/components/DeelnemerJourney";
import StickyPilotCta from "@/components/StickyPilotCta";
import usePageMeta from "@/hooks/usePageMeta";
import { buildPilotMailto, ASSET_BROCHURE_URL, ASSET_VIGOR_URL, ASSET_FLYER_URL, ASSET_QA_URL } from "@/lib/mailto";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1484627147104-f5197bcd6651?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1723227123897-0624fe046ae8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

const MEERWAARDE = [
  "Bereik van inwoners die onvoldoende aansluiting vinden.",
  "Ontwikkeling van zelfinzicht en eigen regie.",
  "Aanwezigheid, deelname en afronding.",
  "Verandering in vaardigheden en participatie.",
  "Passende vervolgstappen naar bestaande voorzieningen.",
  "Behoud van ontwikkeling na het programma.",
];

const LKS_FAQ = {
  q: "Hoe sluit LevelLeven aan op loonkostensubsidie?",
  a: [
    "LevelLeven is zelf geen loonkostensubsidieregeling. De methodiek richt zich op persoonlijke ontwikkeling en mogelijke voorbereiding op participatie of werk.",
    "Wanneer een deelnemer later aan de voorwaarden voldoet, kan een werkgever mogelijk gebruikmaken van loonkostensubsidie. LevelLeven wil aansluiten op de bestaande gemeentelijke begeleiding en arbeidsmarktpartners.",
    "Let op: loonkostensubsidie financiert het START-programma niet automatisch.",
  ],
};

const downloads = [
  {
    id: GEMEENTEN.downloadBrochure,
    icon: FileText,
    label: "Brochure (PDF)",
    hint: "Volledige programmabrochure",
    href: ASSET_BROCHURE_URL,
    external: true,
  },
  {
    id: GEMEENTEN.downloadVigor,
    icon: FileText,
    label: "LevelLeven × VIGOR (PDF)",
    hint: "Van beweging naar geborgde skills",
    href: ASSET_VIGOR_URL,
    external: true,
  },
  {
    id: GEMEENTEN.downloadFlyer,
    icon: ImageIcon,
    label: "One-pager (PNG)",
    hint: "Het hele verhaal op één A4",
    href: ASSET_FLYER_URL,
    external: true,
  },
  {
    id: GEMEENTEN.downloadQA,
    icon: HelpCircle,
    label: "Vraag & antwoord (PDF)",
    hint: "Alle veelgestelde vragen op een rij",
    href: ASSET_QA_URL,
    external: true,
  },
  {
    id: GEMEENTEN.downloadPilot,
    icon: Mail,
    label: "Pilotvoorstel op maat",
    hint: "Vraag rechtstreeks aan",
    href: buildPilotMailto({
      subject: "Aanvraag pilotvoorstel LevelLeven",
      body:
        "Beste LevelLeven,\n\nGraag ontvangen wij een pilotvoorstel op maat voor onze gemeente.\n\nGemeente:\nContactpersoon:\nFunctie:\nTelefoon:\n\nMet vriendelijke groet,",
    }),
    external: true,
  },
];

export default function Gemeenten() {
  const { perspective } = usePerspective();
  usePageMeta({
    title: "Voor gemeenten, LevelLeven",
    description:
      "LevelLeven START voor gemeenten: 12 weken, 20–22 deelnemers, 2 trainers. Een aanvullende ontwikkelfase voor inwoners die langdurig stilstaan. Zes pijlers, eerlijke meting, geen beloofde besparingen.",
  });

  const heroCopy = perspective === "deelnemer"
    ? {
        eyebrow: "Voor gemeenten · maar jij bent welkom",
        headline: (
          <>
            Dit is de{" "}
            <span className="italic text-ll-coral relative inline-block">
              zakelijke pagina
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>.
          </>
        ),
        belofte: "Het leven mag ondertussen ook gewoon weer leuk worden.",
        sub: "Deze pagina is geschreven voor gemeenten en professionals. Als jij nadenkt om zelf mee te doen, ga dan naar Voor jou — daar praten we tegen jou, niet over jou.",
        ctaPrimary: { label: "Ga naar Voor jou", to: "/voor-jou", testid: "hero-cta-redirect" },
        ctaSecondary: { label: "Toch de gemeentepagina lezen", to: "#businesscase" },
      }
    : {
        eyebrow: "Voor gemeenten",
        headline: (
          <span className="block text-[38px] sm:text-5xl lg:text-[68px] leading-[1.02]">
            Uw gemeente doet al veel.{" "}
            <span className="italic text-ll-coral relative inline-block">
              Maar wordt iedere inwoner
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>{" "}
            daarmee passend bereikt?
          </span>
        ),
        belofte: "Gemeenten beschikken over verschillende vormen van activering, begeleiding en re-integratie.",
        sub: "LevelLeven gaat er niet van uit dat deze ondersteuning ontbreekt. Wij onderzoeken juist welke inwoners ondanks het bestaande aanbod onvoldoende vooruitkomen en welke aanvullende ontwikkelruimte zij nodig hebben.",
        ctaPrimary: { label: "Plan een verkennend gemeentegesprek", href: buildPilotMailto({ subject: "Verkennend gemeentegesprek LevelLeven" }), testid: "hero-mailto-pilot" },
        ctaSecondary: { label: "Download brochure", href: ASSET_BROCHURE_URL, external: true, testid: "hero-download-brochure" },
      };

  return (
    <div id="businesscase" className="bg-ll-cream text-ll-ink">
      {/* HERO — perspective-aware */}
      <AnimatePresence mode="wait">
        <motion.div
          key={perspective}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <SpeelruimteBanner
            eyebrow={heroCopy.eyebrow}
            headline={heroCopy.headline}
            belofte={heroCopy.belofte}
            subhead={heroCopy.sub}
            chips={perspective === "opdrachtgever" ? ["LevelLeven START · 12 weken", "20–22 deelnemers", "2 trainers", "Start-, tussen- en eindmeting"] : undefined}
            ctaPrimary={heroCopy.ctaPrimary}
            ctaSecondary={heroCopy.ctaSecondary}
            images={HERO_IMGS}
            tone={perspective === "deelnemer" ? "yellow" : "coral"}
          />
        </motion.div>
      </AnimatePresence>

      {/* ZES PIJLERS */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]" data-testid="gemeenten-pijlers">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>De zes pijlers van LevelLeven</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Waarin LevelLeven{" "}
              <span className="italic text-[#E06B3F]">zich wil onderscheiden.</span>
            </h2>
          </div>
          <ZesPijlers />
        </div>
      </section>

      {/* CALLOUT , we vervangen niet wat er al is */}
      <section className="pt-24 sm:pt-32 pb-8">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl">
            <blockquote className="relative border-l-4 border-[#E06B3F] pl-8 sm:pl-10" data-testid="gemeenten-statement-callout">
              <p className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#1F5132] font-light">
                We vervangen niet wat er al is.{" "}
                <span className="italic text-[#E06B3F]">
                  We onderzoeken samen hoe de aansluiting beter kan.
                </span>
              </p>
              <footer className="mt-6 text-xs uppercase tracking-[0.3em] text-[#3E4A42]">
                Aanvullend op wijkteams, welzijn en re-integratie
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* BEOOGDE MEERWAARDE */}
      <section className="py-16 sm:py-24" data-testid="gemeenten-meerwaarde">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel>Beoogde meerwaarde</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Wat uw gemeente met een pilot{" "}
              <span className="italic text-[#E06B3F]">kan onderzoeken.</span>
            </h2>
            <p className="mt-6 text-lg text-[#1F5132] font-light leading-relaxed">
              Dit zijn beoogde en te onderzoeken effecten. Geen bewezen resultaten en geen garantie op uitstroom of besparing.
            </p>
          </div>
          <ol className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {MEERWAARDE.map((m, i) => (
              <li key={m} className="bg-white rounded-2xl p-6 border border-[#1F5132]/10 flex gap-4">
                <span className="chapter-num text-lg text-[#E06B3F]">0{i + 1}</span>
                <span className="text-[#1F5132] leading-relaxed">{m}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PROGRAMMA-ARCHITECTUUR */}
      <section className="py-24 sm:py-32 bg-[#1F5132] text-[#FAF7F1]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <ProgrammaArchitectuur perspective="opdrachtgever" />
        </div>
      </section>

      {/* KOSTENINDICATIE */}
      <section className="py-24 sm:py-28" data-testid="gemeenten-kosten">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <SectionLabel>Indicatieve kosten</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Circa €5.000{" "}
              <span className="italic text-[#E06B3F]">per deelnemer.</span>
            </h2>
            <p className="mt-6 text-[#1F5132] font-light leading-relaxed">
              Een voorlopig uitgangspunt voor de gemeentelijke START-variant, afhankelijk van de definitieve uitvoering en afspraken.
              Voor VERDER noemen we nog geen prijs: de specificaties zijn nog niet vastgesteld.
            </p>
          </div>
          <div className="lg:col-span-6 rounded-3xl bg-[#F0EAD8] p-8 sm:p-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3E4A42]">Rekenvoorbeeld</p>
            <p className="mt-4 chapter-num text-4xl sm:text-5xl text-[#1F5132]" data-testid="gemeenten-rekenvoorbeeld">20 × €5.000 = €100.000</p>
            <p className="mt-3 text-sm text-[#1F5132]/80">Indicatieve groepskosten bij 20 deelnemers. Bij 22 deelnemers: €110.000.</p>
            <Link to="/programmabouwer" data-testid="gemeenten-programmabouwer-link" className="mt-6 inline-flex items-center gap-2 font-heading italic text-lg text-[#E06B3F] hover:text-[#1F5132] transition-colors">
              Maak uw eigen raming in de Programmabouwer <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ONDERZOEK */}
      <OnderzoekKaarten />

      {/* PILOTPLANNING */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl mb-16 reveal">
            <SectionLabel>Pilotplanning · 12 weken START</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Helder. Inpasbaar.{" "}
              <span className="italic text-[#E06B3F]">Verantwoordbaar.</span>
            </h2>
          </div>
          <div className="reveal">
            <PilotTimeline />
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-4 gap-4">
            {[
              { t: "Startmeting", d: "Bij aanvang: zelfregie, dagstructuur, vertrouwen, sociale vaardigheden, perspectief." },
              { t: "Tussenmeting", d: "Een of meer momenten tijdens START. Ook aanwezigheid en deelname." },
              { t: "Eindmeting", d: "Na week 12: ontwikkeling en de gekozen vervolgstap." },
              { t: "Vervolgmeting", d: "Waar afgesproken: houdt de ontwikkeling stand na het programma?" },
            ].map((m) => (
              <div key={m.t} className="reveal bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-6">
                <p className="font-heading text-xl text-[#1F5132]">{m.t}</p>
                <p className="mt-2 text-sm text-[#1F5132] leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POSITIE IN DE KETEN */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12 items-end">
            <div className="lg:col-span-6 reveal">
              <SectionLabel>Positie in de keten</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
                Geen parallel systeem.{" "}
                <span className="italic text-[#E06B3F]">
                  Een ontbrekende schakel.
                </span>
              </h2>
            </div>
            <div className="lg:col-span-6 reveal">
              <p className="text-[#1F5132] font-light leading-relaxed">
                LevelLeven vult de fase die tussen uitkeringsbestand en
                vervolgtrajecten ontbreekt, aanvullend op wijkteams,
                welzijnsorganisaties en re-integratiebureaus.
              </p>
            </div>
          </div>
          <div className="reveal">
            <PositieInKeten />
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4 reveal">
              <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">
                Integraal en domeinoverstijgend
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl text-[#1F5132] leading-snug font-light">
                Vijf domeinen. Eén samenhangende aanpak.
              </h3>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 reveal">
              <IntegraalDomeinen />
              <p className="mt-6 text-[#1F5132] font-light leading-relaxed">
                LevelLeven verbindt deze domeinen zonder dat de gemeente een
                nieuw systeem hoeft op te zetten. Direct inpasbaar in een
                integrale benadering van het sociaal domein.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROEFLES VOOR BEGELEIDERS */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 reveal">
              <SectionLabel>Proefles voor begeleiders</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
                Eerst zelf ervaren.{" "}
                <span className="italic text-[#E06B3F]">Dan besluiten.</span>
              </h2>
              <p className="mt-6 text-[#1F5132] font-light leading-relaxed">
                Plan met 6 tot 8 begeleiders uit uw gemeente een proefles.
                U ervaart in één dagdeel hoe de LevelLeven-methodiek werkt,
                wat er in een groep gebeurt, hoe wij vertrouwen bouwen en
                hoe reflectie tot beweging leidt.
              </p>
            </div>

            <div className="md:col-span-7 reveal">
              <div className="bg-[#FAF7F1] rounded-2xl border border-[#1F5132]/10 p-8 sm:p-10">
                <ul className="space-y-4">
                  {[
                    { t: "Groepsgrootte", d: "6 – 8 begeleiders uit uw team." },
                    { t: "Duur", d: "Eén dagdeel (± 3 uur), inclusief nabespreking." },
                    { t: "Wat u ervaart", d: "Een verkorte versie van blok 1 (fundament): verlangen, zelfbeeld en contact." },
                    { t: "Wie geeft het", d: "Twee NLP-gecertificeerde trainers van LevelLeven." },
                  ].map((r) => (
                    <li key={r.t} className="flex items-start gap-4">
                      <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#E06B3F] shrink-0" />
                      <div>
                        <p className="font-heading text-lg text-[#1F5132]">{r.t}</p>
                        <p className="text-[#1F5132] font-light">{r.d}</p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 border-t border-[#1F5132]/10 pt-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                    Belangrijk om te weten
                  </p>
                  <p className="text-[#1F5132] font-light leading-relaxed">
                    De proefles is een <span className="text-[#1F5132]">standaard onderdeel</span>{" "}
                    van elk programma dat een gemeente bij ons afneemt.
                    Zo maken uw begeleiders de methodiek eigen en kunnen
                    zij naadloos aansluiten op wat er in de groep gebeurt.
                  </p>
                </div>

                <div className="mt-8">
                  <a
                    data-testid="gemeenten-proefles-cta"
                    href={buildPilotMailto({
                      subject: "Aanmelding proefles LevelLeven (begeleiders)",
                      body:
                        "Beste LevelLeven,\n\nWij willen ons graag aanmelden voor een proefles voor begeleiders (6-8 personen).\n\nGemeente:\nContactpersoon:\nFunctie:\nAantal begeleiders:\nVoorkeurdatum(s):\nTelefoon:\n\nMet vriendelijke groet,",
                    })}
                    className="group inline-flex items-center gap-4 bg-[#1F5132] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-7 pr-2 py-2 transition-colors duration-300"
                  >
                    <span className="text-base font-medium">Plan een proefles</span>
                    <span className="bg-[#E06B3F] group-hover:bg-[#FAF7F1] group-hover:text-[#1F5132] text-white w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                      <ArrowUpRight size={18} />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SARAH, DEELNEMER JOURNEY */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-14 max-w-2xl reveal">
            <SectionLabel>Ontwikkeling in beeld</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Hoe een route{" "}
              <span className="italic text-[#E06B3F]">eruit kan zien</span>.
            </h2>
            <p className="mt-6 text-[#1F5132] font-light">
              Fictieve deelnemers ter illustratie. De eerste twaalf weken zijn
              START; daarna volgt een mogelijk vervolg via bestaande
              voorzieningen. Dit zijn geen echte resultaten.
            </p>
          </div>
          <div className="reveal">
            <DeelnemerJourney />
          </div>
        </div>
      </section>

      {/* NEDERLAND-KAART */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-14 max-w-2xl reveal">
            <SectionLabel>Landelijk actief</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Overal in Nederland{" "}
              <span className="italic text-[#E06B3F]">inzetbaar</span>.
            </h2>
            <p className="mt-6 text-[#1F5132] font-light">
              Zoek uw stad of tik op een cirkel op de kaart om te zien hoeveel
              inwoners een bijstandsuitkering hebben, de mensen voor wie
              LevelLeven het fundament bouwt. Ook buiten pilot-regio's kunnen
              inwoners zich op een wachtlijst plaatsen.
            </p>
          </div>
          <div className="reveal">
            <LeafletKaart />
          </div>
        </div>
      </section>

      {/* FAQ LOONKOSTENSUBSIDIE */}
      <section className="py-20 sm:py-24 bg-[#F0EAD8]" data-testid="gemeenten-faq-lks">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionLabel>Veelgestelde vraag</SectionLabel>
          <details open className="group rounded-3xl bg-[#FAF7F1] border border-[#1F5132]/10 p-7 sm:p-10">
            <summary className="cursor-pointer list-none font-heading text-2xl sm:text-3xl text-[#1F5132] font-light">{LKS_FAQ.q}</summary>
            <div className="mt-6 space-y-4 text-[#1F5132] leading-relaxed">
              {LKS_FAQ.a.map((p, i) => <p key={i} className={i === 2 ? "font-medium" : ""}>{p}</p>)}
            </div>
          </details>
        </div>
      </section>

      {/* AFSLUITENDE UITNODIGING */}
      <section className="py-24 sm:py-32 bg-[#1F5132] text-[#FAF7F1]" data-testid="gemeenten-uitnodiging">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <h2 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04] max-w-4xl">
            Laten we samen onderzoeken{" "}
            <span className="italic text-[#F0B98D]">welke inwoners nog onvoldoende worden bereikt.</span>
          </h2>
          <a
            href={buildPilotMailto({ subject: "Verkennend gemeentegesprek LevelLeven" })}
            data-testid="gemeenten-cta-verkennend"
            className="mt-12 group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#F0B98D] hover:text-[#1F5132] text-white rounded-full pl-8 pr-2 py-2 transition-colors duration-300"
          >
            <span className="text-base font-medium">Plan een verkennend gemeentegesprek</span>
            <span className="bg-white text-[#1F5132] w-12 h-12 rounded-full flex items-center justify-center"><ArrowUpRight size={20} /></span>
          </a>
        </div>
      </section>

      {/* DOWNLOADS + CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-16 max-w-2xl reveal">
            <SectionLabel>Documenten</SectionLabel>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
              Brochures, one-pager en vragenlijst.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {downloads.map((d) => {
              const Icon = d.icon;
              return (
                <a
                  key={d.label}
                  href={d.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={d.id}
                  className="reveal group bg-[#FAF7F1] hover:bg-[#1F5132] hover:text-[#FAF7F1] rounded-2xl p-7 transition-colors duration-300 border border-[#1F5132]/10"
                >
                  <div className="w-12 h-12 rounded-full bg-[#E06B3F]/10 group-hover:bg-[#E06B3F] text-[#E06B3F] group-hover:text-white flex items-center justify-center mb-6 transition-colors">
                    <Icon size={20} />
                  </div>
                  <p className="font-heading text-2xl leading-tight">{d.label}</p>
                  <p className="mt-2 text-sm text-[#1F5132] group-hover:text-[#FAF7F1]/70">{d.hint}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm">
                    Openen <ArrowUpRight size={16} />
                  </span>
                </a>
              );
            })}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row gap-4 reveal">
            <a
              href={buildPilotMailto()}
              data-testid={GEMEENTEN.ctaKennismaking}
              className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#1F5132] text-white rounded-full pl-8 pr-2 py-2 transition-colors duration-300"
            >
              <span className="text-base font-medium">Plan een pilotgesprek via e-mail</span>
              <span className="bg-white text-[#1F5132] w-12 h-12 rounded-full flex items-center justify-center">
                <ArrowUpRight size={20} />
              </span>
            </a>
            <Link
              to="/contact"
              data-testid={GEMEENTEN.ctaPilot}
              className="inline-flex items-center gap-3 border border-[#1F5132] text-[#1F5132] hover:bg-[#1F5132] hover:text-white rounded-full px-7 py-4 transition-colors duration-300"
            >
              Of via het contactformulier
            </Link>
          </div>
        </div>
      </section>
      <StickyPilotCta />
    </div>
  );
}
